// FunnelMetricsCard — US-135.
//
// Wraps the DS `Funnel` primitive with the 5-step LinkedIn funnel
// (Leads → Invités → Acceptés → Répondu → 🔥 Très chauds). Hot leads use
// the `error` tone to mirror the prototype's red accent.

import type { Campaign } from '@barel/api-client/react';
import { useT } from '@barel/i18n';
import { Card, Funnel, type FunnelStepData } from '@barel/ui';

export interface FunnelMetricsCardProps {
  campaign: Pick<
    Campaign,
    'leadsCount' | 'invitedCount' | 'acceptedCount' | 'repliedCount' | 'hotCount'
  >;
}

export function FunnelMetricsCard({ campaign }: FunnelMetricsCardProps) {
  const t = useT('campaigns');
  const steps: FunnelStepData[] = [
    { id: 'leads', label: t('funnel.leads'), value: campaign.leadsCount ?? 0, tone: 'neutral' },
    {
      id: 'invited',
      label: t('funnel.invited'),
      value: campaign.invitedCount ?? 0,
      tone: 'accent',
    },
    {
      id: 'accepted',
      label: t('funnel.accepted'),
      value: campaign.acceptedCount ?? 0,
      tone: 'success',
    },
    {
      id: 'replied',
      label: t('funnel.replied'),
      value: campaign.repliedCount ?? 0,
      tone: 'accent',
    },
    {
      id: 'hot',
      label: t('funnel.hot'),
      value: campaign.hotCount ?? 0,
      tone: (campaign.hotCount ?? 0) > 0 ? 'error' : 'neutral',
    },
  ];

  return (
    <Card padding="lg" radius="2xl" data-testid="funnel-card">
      <div className="text-tertiary text-xs font-bold uppercase mb-3">
        {t('detail.funnelTitle')}
      </div>
      <Funnel steps={steps} aria-label={t('detail.funnelAria') as string} />
    </Card>
  );
}
